import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { TypedMemo } from '../common/types';
import { useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';
import { useUserTokenDecoded } from '../../redux/slices/appInfo/hooks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole handoff; each part uses its own slice of it */
import styles from './MobileApps.module.scss';
/* eslint-enable css-modules/no-unused-class */

const bold = (chunks: React.ReactNode[]) => <b>{chunks}</b>;

/** The server sends one link mail per minute, so the button stays off for the same time. */
const RESEND_AFTER_MS = 60_000;

interface EmailLinkButtonProps {
  next?: string;
}

/** "Email me the link" (design 1a, 1b; sent state 2b). The mail holds the same handoff link. */
const EmailLinkButtonComponent: React.FC<EmailLinkButtonProps> = ({ next }) => {
  const intl = useIntl();
  const trpcClient = useTRPCClientAPI();
  const email = useUserTokenDecoded()?.email ?? '';
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  const send = useTRPCMutation({
    mutationFn: () => trpcClient.users.sendMobileAppLink.mutate({ next }),
    onSuccess: () => setIsCoolingDown(true),
  });

  useEffect(() => {
    if (!isCoolingDown) {
      return undefined;
    }
    const timer = window.setTimeout(() => setIsCoolingDown(false), RESEND_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [isCoolingDown]);

  const onClick = useCallback(() => {
    send.mutate();
  }, [send]);

  return (
    <>
      <button type="button" className={styles.textButton} onClick={onClick} disabled={send.isPending || isCoolingDown}>
        <FontAwesomeIcon icon={faEnvelope} fixedWidth />
        {isCoolingDown
          ? intl.formatMessage({ id: 'app.sml.mobileApps.email.sent', defaultMessage: 'Sent' })
          : intl.formatMessage({ id: 'app.sml.mobileApps.email.send', defaultMessage: 'Email me the link' })}
      </button>
      {send.isSuccess && (
        <Alert color="success" className="mb-0">
          {intl.formatMessage(
            {
              id: 'app.sml.mobileApps.email.sentNote',
              defaultMessage: 'We sent the link to <b>{email}</b>. Open the email on your phone and tap the link.',
            },
            { email, b: bold }
          )}
        </Alert>
      )}
      {send.isError && (
        <Alert color="danger" className="mb-0">
          {intl.formatMessage({
            id: 'app.sml.mobileApps.email.failed',
            defaultMessage: 'We could not send the email. Wait one minute and try again.',
          })}
        </Alert>
      )}
    </>
  );
};

export const EmailLinkButton = TypedMemo(EmailLinkButtonComponent);
